
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useLocation } from "react-router-dom";

type Tenant = {
  id: string;
  name: string;
  subdomain?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  created_at?: string;
  updated_at?: string;
  plan?: string;
  site_template?: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  subscription_status?: string | null;
  current_period_end?: string | null;
  trial_end?: string | null;
  cancel_at_period_end?: boolean | null;
} | null;

interface TenantContextType {
  tenantId: string | null;
  tenant: Tenant;
  loading: boolean;
  error: string | null;
  role: "owner" | "admin" | "member" | null;
  refresh: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const params = useParams();
  const location = useLocation();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenant, setTenant] = useState<Tenant>(null);
  const [role, setRole] = useState<"owner" | "admin" | "member" | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract tenant slug from URL or subdomain
  const tenantSlug = params.tenantSlug;
  
  // Extract subdomain from hostname
  const getSubdomainFromHost = () => {
    const host = window.location.hostname;
    
    console.log('[TenantProvider] Host detection:', {
      hostname: host,
      protocol: window.location.protocol,
      fullUrl: window.location.href
    });
    
    // Remove 'www.' if present
    const cleanHost = host.replace(/^www\./, '');
    
    // Check if it's the main domain
    const tenantDomain = 'imobpanda.site';
    
    // Se for localhost ou IP, não é subdomínio
    if (cleanHost.includes('localhost') || cleanHost.match(/^\d+\.\d+\.\d+\.\d+/)) {
      console.log('[TenantProvider] Localhost ou IP detectado, sem subdomain');
      return null;
    }
    
    if (cleanHost === tenantDomain) {
      console.log('[TenantProvider] Domínio principal detectado');
      return null; // Main domain, no subdomain
    }
    
    // Check if it's a subdomain for imobpanda.site or imobpanda.com
    const parts = cleanHost.split('.');
    
    // Check for app.imobpanda.com explicitly
    if (cleanHost === 'app.imobpanda.com') {
      console.log('[TenantProvider] App subdomain detectado (imobpanda.com)');
      return 'app';
    }
    
    // Check for tenant subdomains (imobpanda.site)
    if (parts.length >= 3 && cleanHost.endsWith(tenantDomain)) {
      // It's a subdomain like claudiogarcia.imobpanda.site
      const subdomain = parts[0];
      console.log('[TenantProvider] Subdomínio detectado:', subdomain);
      return subdomain;
    }
    
    console.log('[TenantProvider] Nenhum subdomínio detectado');
    return null;
  };
  
  const subdomain = getSubdomainFromHost();

  const loadTenant = async () => {
    setLoading(true);
    setError(null);

    console.log("[TenantProvider] Loading tenant - tenantSlug:", tenantSlug, "subdomain:", subdomain, "user:", user?.id);
    console.log("[TenantProvider] Current hostname:", window.location.hostname);
    console.log("[TenantProvider] Current URL:", window.location.href);

    // 🔥 FIX: Ignore "app" subdomain - it's not a tenant subdomain
    if (subdomain === 'app') {
      console.log("[TenantProvider] App subdomain detected - skipping subdomain loading, will load by user membership");
      
      // For app.imobpanda.com, load tenant by user membership only
      if (user) {
        console.log("[TenantProvider] Loading tenant for authenticated user:", user.id);

        const { data: memberships, error: membershipsError } = await supabase
          .from("tenant_members")
          .select("tenant_id, role, created_at")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true });

        if (membershipsError) {
          console.error("[TenantProvider] Error loading memberships:", membershipsError);
          setError("Falha ao carregar o tenant.");
          setLoading(false);
          return;
        }

        const membership = memberships?.[0];
        if (!membership?.tenant_id) {
          console.warn("[TenantProvider] Nenhum tenant encontrado para o usuário");
          
          // O usuário não tem mais vínculo (ex: foi removido da equipe).
          // Precisamos limpar o tenant_id do profile dele e redirecioná-lo para o onboarding
          try {
            await supabase
              .from('profiles')
              .update({ tenant_id: null })
              .eq('user_id', user.id);
              
            // Também limpar o cache de onboarding
            await supabase.auth.updateUser({
              data: { onboarding_completed: false }
            });
          } catch (error) {
            console.error("[TenantProvider] Erro ao limpar vínculo:", error);
          }
          
          setTenantId(null);
          setTenant(null);
          setRole(null);
          setLoading(false);
          
          // Redirecionar para onboarding para que ele crie sua própria conta se quiser
          if (window.location.pathname !== '/onboarding') {
            window.location.href = '/onboarding';
          }
          return;
        }

        setTenantId(membership.tenant_id);
        setRole(membership.role as "owner" | "admin" | "member");

        // Carregar dados do tenant
        const { data: tenantData, error: tenantError } = await supabase
          .from("tenants")
          .select("*")
          .eq("id", membership.tenant_id)
          .maybeSingle();

        if (tenantError) {
          console.error("[TenantProvider] Error loading tenant:", tenantError);
          setError("Falha ao carregar os dados do tenant.");
          setTenant(null);
          setLoading(false);
          return;
        }

        setTenant((tenantData as Tenant) ?? null);
        setLoading(false);
        console.log("[TenantProvider] Loaded tenant for user in app subdomain:", tenantData);
        return;
      } else {
        // No user in app subdomain - this is expected for login page
        console.log("[TenantProvider] No user in app subdomain - user needs to login");
        setTenantId(null);
        setTenant(null);
        setRole(null);
        setLoading(false);
        return;
      }
    }

    // 1. PRIORIDADE: Se há subdomain (que não é "app"), buscar por ele
    if (subdomain) {
      console.log("[TenantProvider] Loading tenant by subdomain:", subdomain);
      
      // 🔒 SEGURANÇA: Usar acesso direto com RLS policies
      // As policies RLS já permitem acesso público a tenants com subdomain
      const result = await supabase
        .from("tenants")
        .select("id, name, subdomain, plan, site_template, created_at, logo_url, primary_color, secondary_color")
        .eq("subdomain", subdomain)
        .maybeSingle();
      
      const tenantData = result.data;
      const tenantError = result.error;

      console.log("[TenantProvider] Query result:", { tenantData, tenantError });

      if (tenantError) {
        console.error("[TenantProvider] Error loading tenant by subdomain:", tenantError);
        setError("Falha ao carregar o tenant.");
        setLoading(false);
        return;
      }

      if (!tenantData) {
        console.warn("[TenantProvider] No tenant found for subdomain:", subdomain);
        
        setError("SUBDOMAIN_NOT_FOUND");
        setTenantId(null);
        setTenant(null);
        setRole(null);
        setLoading(false);
        return;
      }

      setTenantId((tenantData as any).id);
      setTenant(tenantData as Tenant);

      // Se há usuário logado, verificar se ele tem role neste tenant
      if (user) {
        const { data: membership } = await supabase
          .from("tenant_members")
          .select("role")
          .eq("user_id", user.id)
          .eq("tenant_id", (tenantData as any).id)
          .maybeSingle();
        
        setRole(membership?.role as "owner" | "admin" | "member" || null);
      } else {
        setRole(null);
      }

      setLoading(false);
      console.log("[TenantProvider] Loaded tenant by subdomain:", tenantData);
      return;
    }

    // 2. Se há tenantSlug na URL, buscar por ele
    if (tenantSlug) {
      console.log("[TenantProvider] Loading tenant by slug:", tenantSlug);
      
      // 🔒 SEGURANÇA: Usar acesso direto com RLS policies
      const result = await supabase
        .from("tenants")
        .select("id, name, subdomain, plan, site_template, created_at, logo_url, primary_color, secondary_color")
        .eq("subdomain", tenantSlug)
        .maybeSingle();
      
      const tenantData = result.data;
      const tenantError = result.error;

      if (tenantError) {
        console.error("[TenantProvider] Error loading tenant by slug:", tenantError);
        setError("Falha ao carregar o tenant.");
        setLoading(false);
        return;
      }

      if (!tenantData) {
        console.warn("[TenantProvider] No tenant found for slug:", tenantSlug);
        setError("TENANT_NOT_FOUND");
        setTenantId(null);
        setTenant(null);
        setRole(null);
        setLoading(false);
        return;
      }

      setTenantId((tenantData as any).id);
      setTenant(tenantData as Tenant);

      // Se há usuário logado, verificar se ele tem role neste tenant
      if (user) {
        const { data: membership } = await supabase
          .from("tenant_members")
          .select("role")
          .eq("user_id", user.id)
          .eq("tenant_id", (tenantData as any).id)
          .maybeSingle();
        
        setRole(membership?.role as "owner" | "admin" | "member" || null);
      } else {
        setRole(null);
      }

      setLoading(false);
      console.log("[TenantProvider] Loaded tenant by slug:", tenantData);
      return;
    }

    // 2. Se há usuário logado mas sem slug, buscar pelo membership
    if (user) {
      console.log("[TenantProvider] Loading tenant for authenticated user:", user.id);

      const { data: memberships, error: membershipsError } = await supabase
        .from("tenant_members")
        .select("tenant_id, role, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });

      if (membershipsError) {
        console.error("[TenantProvider] Error loading memberships:", membershipsError);
        setError("Falha ao carregar o tenant.");
        setLoading(false);
        return;
      }

      const membership = memberships?.[0];
      if (!membership?.tenant_id) {
        console.warn("[TenantProvider] Nenhum tenant encontrado para o usuário");
        
        // Redirecionar para onboarding para que ele crie sua própria conta
        if (window.location.pathname !== '/onboarding') {
          window.location.href = '/onboarding';
        }
        
        setTenantId(null);
        setTenant(null);
        setRole(null);
        setLoading(false);
        return;
      }

      setTenantId(membership.tenant_id);
      setRole(membership.role as "owner" | "admin" | "member");

      // Carregar dados do tenant
      const { data: tenantData, error: tenantError } = await supabase
        .from("tenants")
        .select("*")
        .eq("id", membership.tenant_id)
        .maybeSingle();

      if (tenantError) {
        console.error("[TenantProvider] Error loading tenant:", tenantError);
        setError("Falha ao carregar os dados do tenant.");
        setTenant(null);
        setLoading(false);
        return;
      }

      setTenant((tenantData as Tenant) ?? null);
      setLoading(false);
      console.log("[TenantProvider] Loaded tenant for user:", tenantData);
      return;
    }

    // 3. FALLBACK: Para usuários não autenticados sem slug, buscar por hostname
    const currentHost = window.location.host;
    console.log("[TenantProvider] Loading tenant for public access, host:", currentHost);

    const { data: tenantSettings, error: tenantError } = await supabase
      .from('public_tenant_settings')
      .select('tenant_id')
      .eq('host', currentHost)
      .maybeSingle();

    if (tenantError) {
      console.error("[TenantProvider] Error loading tenant by host:", tenantError);
      setError("Falha ao carregar o tenant.");
      setLoading(false);
      return;
    }

    if (!tenantSettings?.tenant_id) {
      console.warn("[TenantProvider] No tenant found for host:", currentHost);
      setTenantId(null);
      setTenant(null);
      setRole(null);
      setLoading(false);
      return;
    }

    setTenantId(tenantSettings.tenant_id);
    setRole(null); // Usuário público não tem role

    // 🔒 SEGURANÇA: Carregar dados do tenant com RLS policies
    const { data: tenantData, error: tenantDataError } = await supabase
      .from("tenants")
      .select("id, name, subdomain, site_template, created_at, logo_url, primary_color, secondary_color")
      .eq("id", tenantSettings.tenant_id)
      .maybeSingle();

    if (tenantDataError) {
      console.error("[TenantProvider] Error loading tenant data:", tenantDataError);
      setError("Falha ao carregar os dados do tenant.");
      setTenant(null);
      setLoading(false);
      return;
    }

    setTenant((tenantData as Tenant) ?? null);
    setLoading(false);
    console.log("[TenantProvider] Loaded tenant for public access:", tenantData);
  };

  useEffect(() => {
    if (!authLoading) {
      void loadTenant();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id, tenantSlug, subdomain]);
  
  // 🔍 Sentry: Atualizar tenant context quando tenant mudar
  useEffect(() => {
    if (typeof window !== 'undefined' && tenant) {
      import('@/lib/sentry').then(({ setContext }) => {
        setContext('tenant', {
          id: tenant.id,
          name: tenant.name,
          subdomain: tenant.subdomain,
        });
      });
    }
  }, [tenant]);

  const value = useMemo<TenantContextType>(
    () => ({
      tenantId,
      tenant,
      loading,
      error,
      role,
      refresh: loadTenant,
    }),
    [tenantId, tenant, loading, error, role]
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return ctx;
}
